import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import z from "zod";
import {
  Alert,
  Button,
  Input,
  Modal,
  PhoneInput,
  toast,
  Typography,
  useWizard,
} from "@picklebet/ui";
import appConfig from "appConfig";
import { parseError } from "@/utilities/errorsParser";
import { walletService } from "@/features/wallet/services/wallet";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { NOT_REGISTERED_FOR_AUTODEPOSIT_CODE } from "@/features/wallet/utils/interac";
import type { WithdrawStepName } from "@/features/wallet/components/withdraw/utils/registry";
import { useWithdrawWizard } from "@/features/wallet/components/withdraw/hooks/useWithdrawWizard";
import { WithdrawContent } from "../../blocks/withdraw-content/WithdrawContent";

import * as styles from "./AddInteracAccountStep.module.scss";

const interacZendeskArticleUrl =
  appConfig.ENV === "production"
    ? "https://picklebet-international.zendesk.com/hc/en-us/articles/16437764666895-Interac?overrideHelpCenter=true"
    : "https://picklebet-international1745290020.zendesk.com/hc/en-us/articles/16437817228687-Interac?overrideHelpCenter=true";

const invalidPhoneMessage = "Please enter a valid mobile number";

const schema = z.object({
  email: z.email(),
  // PhoneInput emits undefined when the field is cleared, so z.string() fails
  // on the type before the refine runs. Without a message here the user sees
  // Zod's raw "expected string, received undefined".
  phone: z
    .string({ error: invalidPhoneMessage })
    .refine((value) => isValidPhoneNumber(value), invalidPhoneMessage),
});

export const AddInteracAccountStep = () => {
  const { goTo } = useWizard<WithdrawStepName>();
  const { canManageWallets } = useWithdrawWizard();
  const [isAutodepositError, setIsAutodepositError] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: "", phone: "" },
  });

  const onSubmit = async ({ email, phone }: z.infer<typeof schema>) => {
    setIsAutodepositError(false);

    const result = await walletService.addInteracBankAccount({ email, phone });

    if (!result.ok) {
      // this one failure carries a Learn More link, so it stays inline
      if (result.error.code === NOT_REGISTERED_FOR_AUTODEPOSIT_CODE) {
        setIsAutodepositError(true);
        return;
      }

      toast({
        variant: "danger",
        title: "Unable to Add Interac Details",
        description: parseError(result.error),
      });
      return;
    }

    goTo("withdraw/interac-account", { replace: true });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <WithdrawContent>
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <Input
              label="Email Address"
              placeholder="joe@example.com"
              errorMessage={errors.email?.message}
              noSpacing
              {...field}
              disabled={!canManageWallets}
            />
          )}
        />

        <PhoneInput
          name="phone"
          control={control}
          label="Mobile Number"
          placeholder="Enter your mobile number"
          errorMessage={errors.phone?.message}
          country="CA"
          withCountryCallingCode
          noSpacing
          disabled={!canManageWallets}
        />

        <Modal.Footer>
          {!canManageWallets && (
            <Alert
              title="Unable to Add Interac Details"
              color="warning"
              indicator="bottom"
              size="small"
            >
              <Typography variant="body" noSpacing>
                You don't have permission to perform this action.
              </Typography>
            </Alert>
          )}

          {isAutodepositError && (
            <Alert
              title="Autodeposit Not Enabled"
              color="danger"
              indicator="bottom"
            >
              <Typography variant="body" noSpacing>
                Please enable Autodeposit to withdraw.{" "}
                <Link
                  to={interacZendeskArticleUrl}
                  className={styles.errorLearnMoreLink}
                >
                  Learn More
                </Link>
              </Typography>
            </Alert>
          )}

          <Button
            className={styles.addButton}
            type="submit"
            loading={isSubmitting}
            disabled={!canManageWallets}
          >
            Add Interac Details
          </Button>
        </Modal.Footer>

        <div className={styles.autodepositNote}>
          <Typography
            className={styles.autodepositLabel}
            variant="body"
            noSpacing
          >
            Autodeposit must be enabled.
          </Typography>
          <Link to={interacZendeskArticleUrl} className={styles.learnMoreLink}>
            Learn More
          </Link>
        </div>
      </WithdrawContent>
    </form>
  );
};
